import { client } from '@/shared/lib/client';
import { isInternalDomain } from '@/utils/const';

/**
 * 用量和计费服务API接口
 */

// API响应类型定义
export interface ApiResponse<T = any> {
  status: number;
  message: string;
  data: T;
}

// 计费相关类型定义
export interface AccountInfo {
  account: {
    id: number;
    userId: string;
    accountType: number; // 1=普通 2=内部 3=VIP
    status: number; // 0=禁用 1=启用
    tierCode: string;
    tierName: string;
  };
  summary: {
    creditTotal: number; // 活跃订阅积分总和
    creditUsed: number; // 活跃订阅已用总和
    creditAvailable: number; // 可用积分
    creditUnit: string; // 计价单位默认积分
    renewAt: number; // 下次积分刷新时间戳
  };
  subscriptions: Array<{
    id: number;
    planCode: string;
    planName: string;
    periodDays: number;
    planKind: number; // 1：普通订阅，2：扩展包
    status: number; // 0=过期 1=生效 2=耗尽
    autoRenew: number; // 是否自动刷新积分 0=非自动 1=自动
    creditTotal: number;
    creditUsed: number;
    creditAvailable: number;
    creditUnit: string;
    effectiveStart: string;
    effectiveEnd: string;
  }>;
}

export interface UsageStatsRequest {
  startTime?: number; // 统计起始时间戳（毫秒，UTC）
  endTime?: number; // 统计结束时间戳（毫秒，UTC）
  page?: number; // 审计列表页码，默认 1
  pageSize?: number; // 审计列表每页条数，默认 20，最大 100
}

export interface UsageStats {
  range: {
    startTime: number; // 毫秒时间戳
    endTime: number;
  };
  pagination: {
    page: number; // 当前页码
    pageSize: number; // 每页条数
    total: number; // 总记录数
    totalPages: number; // 总页数
  };
  usageList: Array<{
    occurredAt: number; // 发生时间
    modelName: string;
    provider: string;
    totalTokens: number; // token 用量
    creditUsed: number; // 积分用量
    creditUnit: string;
    creditBalanceAfter: number; // 剩余额度
  }>;
}

/**
 * 查询账户信息
 * @returns Promise<ApiResponse<AccountInfo>>
 */
export const getAccountInfo = async (): Promise<ApiResponse<AccountInfo>> => {
  try {
    // 根据域名类型添加对应的前缀
    const url = isInternalDomain()
      ? '/api/v1/billing/account'
      : '/api/proxy/api/v1/billing/account';

    const response = (await client({
      url,
      method: 'GET',
    })) as ApiResponse<AccountInfo>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get account info');
  }
};

/**
 * 用量统计
 * @param params 查询参数
 * @returns Promise<ApiResponse<UsageStats>>
 */
export const getUsageStats = async (
  params: UsageStatsRequest,
): Promise<ApiResponse<UsageStats>> => {
  try {
    const queryParams = new URLSearchParams({
      ...(params.startTime && { startTime: params.startTime.toString() }),
      ...(params.endTime && { endTime: params.endTime.toString() }),
      ...(params.page && { page: params.page.toString() }),
      ...(params.pageSize && { pageSize: params.pageSize.toString() }),
    });

    // 根据域名类型添加对应的前缀
    const baseUrl = isInternalDomain()
      ? '/api/v1/billing/usage/stats'
      : '/api/proxy/api/v1/billing/usage/stats';

    const response = (await client({
      url: `${baseUrl}?${queryParams.toString()}`,
      method: 'GET',
    })) as ApiResponse<UsageStats>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get usage stats');
  }
};
